import {assertAndContinue} from 'Asserts';
import {
  isRequiredLinkTagSchema,
  SchemaField,
  SchemaFieldEdge,
  SchemaFieldPath,
} from 'schemas/fields/SchemaFieldConstants';
import * as logging from 'util/Logging';

export enum EdgeDirection {
  // An edge that goes from the current schema to the next one in the path
  OUT = 'OUT',
  // An edge that goes from the next schema in the path to the current one
  IN = 'IN',
}

type PathWithDirections = Array<{label: string | undefined; direction: EdgeDirection | null}>;

interface DirectionalPathAndFieldNames {
  path: PathWithDirections;
  fieldNames: Array<string>;
}

export function constructPath(
  sourceTagSchemaId: string,
  fieldPath: Array<SchemaFieldEdge>
): DirectionalPathAndFieldNames {
  // Constructs a path from schema field edges, which are unordered, starting with the node that corresponds
  // to sourceTagSchemaId, by chaining connected edges to each other.
  // The method assumes that all the edges are reachable from the first node, otherwise an error is raised.
  const visitedEdgeIds: Array<number> = [];
  const resultPath: PathWithDirections = [];
  const edgeFields: Array<string> = [];

  let currentTagSchemaId: string | undefined = sourceTagSchemaId;

  const findNextOutgoingEdge = (): SchemaFieldEdge | undefined =>
    fieldPath.find((edge) => edge.from?.id === currentTagSchemaId && !visitedEdgeIds.includes(edge.id));

  const findNextIncomingEdge = (): SchemaFieldEdge | undefined =>
    fieldPath.find((edge) => edge.to?.id === currentTagSchemaId && !visitedEdgeIds.includes(edge.id));

  while (visitedEdgeIds.length < fieldPath.length) {
    assertAndContinue(
      currentTagSchemaId != null,
      'Reached an edge with no target schema before visiting all edges in the input path'
    );

    let edgeId;
    let prevName;
    let nextName;
    let direction;

    const outgoingEdge = findNextOutgoingEdge();
    const incomingEdge = findNextIncomingEdge();

    if (outgoingEdge) {
      edgeId = outgoingEdge.id;
      prevName = outgoingEdge.from?.name;
      nextName = outgoingEdge.to?.name;
      direction = EdgeDirection.OUT;
      currentTagSchemaId = outgoingEdge.to?.id;
      edgeFields.push(outgoingEdge.name);
    } else if (incomingEdge) {
      edgeId = incomingEdge.id;
      prevName = incomingEdge.to?.name;
      nextName = incomingEdge.from?.name;
      direction = EdgeDirection.IN;
      currentTagSchemaId = incomingEdge.from?.id;
      edgeFields.push(incomingEdge.name);
    } else {
      const msg = `Invalid SchemaField path! Couldn't find next edge after ${currentTagSchemaId}`;
      logging.error(`${msg} (logged error; there should also be an exception with the same message)`, {
        extra: {sourceTagSchemaId, fieldPath},
      });
      throw new Error(msg);
    }
    visitedEdgeIds.push(edgeId);
    if (resultPath.length === 0) {
      // This is the first node in the path - add the other side of the edge to the path as well
      resultPath.push({
        label: prevName,
        direction: null,
      });
    }
    resultPath.push({
      label: nextName,
      direction,
    });
  }
  return {path: resultPath, fieldNames: edgeFields};
}

export function displayPath({
  sourceTagSchemaId,
  tagSchemaFieldEdges,
  includeFields = false,
}: {
  sourceTagSchemaId: string;
  tagSchemaFieldEdges: Array<SchemaFieldEdge>;
  includeFields?: boolean;
}): string {
  const directionalPathAndFieldNames = constructPath(sourceTagSchemaId, tagSchemaFieldEdges);
  const fieldNames = directionalPathAndFieldNames.fieldNames;
  let str = '';
  // There will always be one fewer fieldName than edge, but it's always displayed with the arrow.
  for (const [edgeIdx, edge] of directionalPathAndFieldNames.path.entries()) {
    if (edge.direction === EdgeDirection.OUT) {
      if (includeFields) {
        str += ` (${fieldNames[edgeIdx - 1]})`;
      }
      str += ` \u2192 ${edge.label}`; // right arrow
    } else if (edge.direction === EdgeDirection.IN) {
      str += ` \u2190`; // left arrow
      if (includeFields) {
        str += ` (${fieldNames[edgeIdx - 1]})`;
      }
      str += ` ${edge.label}`;
    } else {
      str += edge.label;
    }
  }
  return str;
}

export function getRequiredLinkEntitySchemaFieldPath(field: SchemaField): SchemaFieldPath | null {
  return field.requiredLink && isRequiredLinkTagSchema(field.requiredLink)
    ? field.requiredLink.entitySchemaFieldPath
    : null;
}
